import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { parse } from 'query-string';
import { MessageInstance } from 'antd/es/message/interface';

import { queryRequest } from 'utils/queryRequest';
import { getWindowLocation } from 'utils/window';

import { DecodedJwt } from '../types';

import { useGuestAccessLinkLabel } from './useGuestAccessLinkLabel';

export const useValidateAndSetToken = ({
  messageApi,
}: {
  messageApi: MessageInstance;
}) => {
  const { t } = useTranslation();
  const guestAccessLinkLabel = useGuestAccessLinkLabel();
  const [errorMessage, setErrorMessage] = useState('');
  const [errorDescription, setErrorDescription] = useState('');
  const [token, setToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [decodedJwt, setDecodedJwt] = useState<DecodedJwt>({});

  const pleaseRetryMsg = t(
    'If someone shared this link with you, please double-check the link you received or ask the sender to re-send the link.',
  );
  const tokenMissingMsg = t(
    'Oops, this {{guestAccessLinkLabel}} is missing a token. {{pleaseRetryMsg}}',
    {
      guestAccessLinkLabel,
      pleaseRetryMsg,
    },
  );
  const invalidTokenFormatMsg = t(
    'Oops, this {{guestAccessLinkLabel}} has an invalid token format. {{pleaseRetryMsg}}',
    {
      guestAccessLinkLabel,
      pleaseRetryMsg,
    },
  );

  const validateAndSetToken = useCallback(
    (tokenToValidate: any) => {
      if (typeof tokenToValidate !== 'string') {
        messageApi.error(t('Token must be a string'));
        setErrorMessage(tokenMissingMsg);
        return;
      }

      try {
        const newlyDecodedJwt: DecodedJwt = jwtDecode(tokenToValidate);
        const { nbf, exp } = newlyDecodedJwt;
        const ts = new Date().getTime() / 1000;

        if (nbf && nbf > ts) {
          // not valid yet
          setErrorMessage(
            t(
              'Oops, this {{guestAccessLinkLabel}} is not valid until {{date}}.',
              {
                guestAccessLinkLabel,
                date: new Date(nbf * 1000).toLocaleString(),
              },
            ),
          );
          setErrorDescription(t('Please refresh this page after that time.'));
        } else if (exp && exp < ts) {
          // expired
          setErrorMessage(
            t('Oops, this {{guestAccessLinkLabel}} expired at {{date}}.', {
              guestAccessLinkLabel,
              date: new Date(exp * 1000).toLocaleString(),
            }),
          );
          setErrorDescription(
            t(
              'If you need continued access to this entry, please request a new {{guestAccessLinkLabel}} from the person who originally sent you this link.',
              { guestAccessLinkLabel },
            ),
          );
        }

        setDecodedJwt(newlyDecodedJwt);
      } catch {
        setErrorMessage(invalidTokenFormatMsg);
      }

      setToken(tokenToValidate);
    },
    [
      guestAccessLinkLabel,
      invalidTokenFormatMsg,
      messageApi,
      t,
      tokenMissingMsg,
    ],
  );

  useEffect(() => {
    async function calculateToken() {
      // We expect the query string to either contain
      // token=LONG_JWT_TOKEN or else shortCode=SHORTCODE - in the
      // token case, that's just a JWT that we can use immediately; in
      // the shortCode case, we need to bounce it back off of helium
      // to look up the full URL associated with that shortCode, and
      // then extract the token out of that URL's query
      // string. Historical context: we originally had the long JWT
      // tokens in the query string as this was simplest, but we are
      // seeing continuing problems with long URLs getting broken or
      // misinterpreted when people try to share them in SMS messages
      // or iMessage etc.; so to avoid that we introduced the
      // shortCode approach which always keeps the user-facing URL
      // short, and the long JWT is only ever seen + consumed inside
      // the javascript in this page.
      const params = parse(getWindowLocation().search);

      // If we have a token directly, use that
      if (params.token) {
        validateAndSetToken(params.token);
        return;
      }

      // Otherwise, we need to get the token from the shortCode
      if (typeof params.shortCode === 'string') {
        setIsLoading(true);

        const currentIdentityLanguage =
          localStorage.getItem('i18nextLng') || 'en';

        // Get fullUrl from shortCode
        const describeShortUrlResponse = await queryRequest({
          apiEndpointName: 'describeShortUrl',
          parameters: [params.shortCode],
          currentIdentityLanguage,
        });

        setIsLoading(false);

        if (describeShortUrlResponse.statusCode >= 400) {
          setErrorMessage(t('An error occurred when trying to get the token'));
          return;
        }

        const { fullUrl } = describeShortUrlResponse.json.data!;
        const [, qs] = fullUrl!.split('?');
        const fullUrlQueryStringParams = parse(qs);

        validateAndSetToken(fullUrlQueryStringParams.token);

        return;
      }

      // We must have a token or shortCode in URL query string params
      setErrorMessage(tokenMissingMsg);
    }

    calculateToken();
  }, [messageApi, t, tokenMissingMsg, validateAndSetToken]);

  return { isLoading, errorMessage, errorDescription, token, decodedJwt };
};
